import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, readJsonResponse } from '../api/client';
import { cacheFetch } from '../api/queryCache';
import FirmwareRiskHistoryChart from '../components/FirmwareRiskHistoryChart';
import { buildVersionTrendMap } from '../components/RiskTrend';
import RiskTrendCell from '../components/RiskTrendCell';
import RisksTopBar from '../components/RisksTopBar';
import StatusVersionsSkeleton from '../components/skeleton/StatusVersionsSkeleton';
import VersionsAllRisksTable from '../components/VersionsAllRisksTable';
import {
  buildDashboardFilterPatch,
  buildFirmwareSearchParams,
  buildFirmwareStatsQuery,
  buildVersionsReportQuery,
  FIRMWARE_RISK_NUMBER_OPTIONS,
  formatFirmwareVersionLabel,
  isAllRisksView,
  parseFirmwareQuery,
  sortFirmwareVersionRows,
} from './firmwareQuery';
import {
  attachAllRisksSeriesToReport,
  loadAllRisksHistoryPayload,
} from './firmwareAllRisksHistory';
import { DEFAULT_FILTERS, FILTER_FETCH_DEBOUNCE_MS, buildDashboardSearchParams } from './dashboardQuery';
import { rememberDashboardSearch, rememberSection, rememberStatusSearch } from './risksTabState';

const GROUP_META = {
  wifi: {
    key: 'wifi',
    label: 'Wi-Fi',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200',
    bar: 'bg-sky-500',
  },
  nbiot: {
    key: 'nbiot',
    label: 'NB-IoT',
    badge: 'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200',
    bar: 'bg-violet-500',
  },
};

function formatCount(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }

  return new Intl.NumberFormat('ru-RU').format(Number(value));
}

function riskShare(atRisk, total) {
  if (!total) {
    return 0;
  }
  return Math.round((Number(atRisk) / Number(total)) * 100);
}

/** Standard risk scale: green → yellow → orange → red. */
function riskPercentStyle(percent) {
  const value = Math.min(100, Math.max(0, Number(percent) || 0));
  if (value <= 0) {
    return { color: 'hsl(142 55% 36%)' };
  }

  // 0% → hue 142 (green), 50% → 45 (yellow/orange), 100% → 0 (red)
  const t = value / 100;
  const hue = Math.round(142 * (1 - t));
  const saturation = Math.round(62 + 28 * t);
  const lightness = Math.round(38 - 6 * t);
  return { color: `hsl(${hue} ${saturation}% ${lightness}%)` };
}

function RiskPercent({ value, className = '' }) {
  if (value == null) {
    return <span className={`tabular-nums text-slate-400 ${className}`}>—</span>;
  }

  return (
    <span className={`tabular-nums font-semibold ${className}`} style={riskPercentStyle(value)}>
      {value}%
    </span>
  );
}

function VersionTable({ group, organizationId, riskNumber, onOpenVersion, trendsByVersion }) {
  const rows = sortFirmwareVersionRows(group?.versions);
  const meta = GROUP_META[group?.key] || GROUP_META.wifi;
  const share = riskShare(group?.at_risk, group?.total);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/70">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-200 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-900/50">
        <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${meta.badge}`}>{meta.label}</span>
        <span className="text-xs text-slate-600 dark:text-slate-300">
          <span className="font-semibold tabular-nums text-slate-900 dark:text-white">
            {formatCount(group?.total)}
          </span>{' '}
          устройств
        </span>
        <span className="text-xs text-slate-500">·</span>
        <span className="text-xs text-slate-600 dark:text-slate-300">
          в рисках{' '}
          <span className="font-semibold tabular-nums text-slate-900 dark:text-white">
            {formatCount(group?.at_risk)}
          </span>
          {group?.total ? (
            <>
              {' '}
              (<RiskPercent value={share} className="text-xs" />)
            </>
          ) : null}
        </span>
        <span className="ml-auto text-[11px] text-slate-400">{formatCount(rows.length)} версий</span>
      </div>

      {group?.total ? (
        <div className="h-1 bg-slate-100 dark:bg-slate-800" aria-hidden="true">
          <div
            className={`h-full ${share > 0 ? '' : meta.bar}`}
            style={{
              width: `${Math.min(100, Math.max(share, share > 0 ? 2 : 100))}%`,
              ...(share > 0 ? { backgroundColor: riskPercentStyle(share).color } : null),
            }}
          />
        </div>
      ) : null}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400 dark:border-slate-800">
            <th className="px-3 py-1.5 font-medium">Версия</th>
            <th className="hidden w-20 px-3 py-1.5 text-right font-medium sm:table-cell">Всего</th>
            <th className="w-24 px-3 py-1.5 text-right font-medium">В рисках</th>
            <th className="w-14 px-3 py-1.5 text-right font-medium">%</th>
            <th
              className="w-14 whitespace-nowrap px-3 py-1.5 text-right font-medium"
              title="К предыдущему снимку 06:00"
            >
              Δ
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-400">
                Нет устройств этого типа связи
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const rowShare = riskShare(row.at_risk, row.total);
              const trend =
                trendsByVersion?.get(row.firmware_version) ||
                trendsByVersion?.get(formatFirmwareVersionLabel(row.firmware_version));

              return (
                <tr
                  key={`${group.key}-${row.firmware_version}`}
                  className="border-b border-slate-50 last:border-0 hover:bg-slate-50/80 dark:border-slate-800/60 dark:hover:bg-slate-900/40"
                >
                  <td className="px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        onOpenVersion({
                          organizationId,
                          firmwareVersion: row.firmware_version,
                          connectionType: group.key,
                          riskNumber,
                        })
                      }
                      className="font-mono text-[13px] font-medium text-slate-800 underline-offset-2 hover:text-sky-700 hover:underline dark:text-slate-100 dark:hover:text-sky-300"
                      title="Открыть сводку с фильтром по этой версии"
                    >
                      {formatFirmwareVersionLabel(row.firmware_version)}
                    </button>
                  </td>
                  <td className="hidden px-3 py-1.5 text-right tabular-nums text-slate-600 dark:text-slate-300 sm:table-cell">
                    {formatCount(row.total)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium text-slate-800 dark:text-slate-100">
                    {formatCount(row.at_risk)}
                  </td>
                  <td className="px-3 py-1.5 text-right text-[13px]">
                    {row.total ? <RiskPercent value={rowShare} /> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right">
                    <RiskTrendCell trend={trend} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Fleet state by firmware version (same source as Dashboard firmware facet). */
export default function StatusPage({ activeTab, onTabChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFirmwareQuery(searchParams), [searchParams]);
  const [organizations, setOrganizations] = useState([]);
  const [data, setData] = useState(null);
  const [allRisksReport, setAllRisksReport] = useState(null);
  const [trends, setTrends] = useState({ wifi: new Map(), nbiot: new Map() });
  const [historyPayloads, setHistoryPayloads] = useState({ wifi: null, nbiot: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const organizationsInitializedRef = useRef(false);
  const loadRequestIdRef = useRef(0);
  const loadedStatsKeyRef = useRef('');
  const statusVisibleRef = useRef(false);
  const showAllRisks = isAllRisksView(filters.riskNumber);

  const applyFilters = useCallback(
    (patch) => {
      const params = buildFirmwareSearchParams({ ...filters, ...patch });
      params.set('tab', 'status');
      rememberStatusSearch(`?${params.toString()}`);
      setSearchParams(params, { replace: true });
    },
    [filters, setSearchParams],
  );

  const openDashboardVersion = useCallback(
    (args) => {
      const currentQuery = searchParams.toString();
      rememberStatusSearch(currentQuery ? `?${currentQuery}` : '');
      const next = buildDashboardSearchParams({
        page: 1,
        pageSize: 50,
        filters: { ...DEFAULT_FILTERS, ...buildDashboardFilterPatch(args) },
      });
      rememberDashboardSearch(`?${next.toString()}`);
      rememberSection('dashboard');
      onTabChange?.('dashboard');
    },
    [onTabChange, searchParams],
  );

  const AllRisksVersionCell = useCallback(
    ({ organizationId, firmwareVersion, connectionType, riskNumber, label, className }) => (
      <button
        type="button"
        onClick={() =>
          openDashboardVersion({
            organizationId,
            firmwareVersion,
            connectionType,
            riskNumber,
          })
        }
        className={
          className ||
          'font-mono text-[13px] font-medium text-slate-800 underline-offset-2 hover:text-sky-700 hover:underline dark:text-slate-100 dark:hover:text-sky-300'
        }
        title="Открыть сводку с фильтром по этой версии"
      >
        {label}
      </button>
    ),
    [openDashboardVersion],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadOrganizations() {
      try {
        const payload = await cacheFetch('organizations', async () => {
          const response = await apiFetch('/api/organizations');
          const body = await readJsonResponse(response, 'Organizations');
          if (!response.ok) throw new Error('Organizations failed');
          return body;
        });
        if (cancelled) {
          return;
        }

        const items = payload.items || [];
        setOrganizations(items);

        if (!organizationsInitializedRef.current && items.length > 0 && !filters.organizationId) {
          organizationsInitializedRef.current = true;
          setSearchParams(
            (current) => {
              const next = new URLSearchParams(current);
              next.set('organizationId', String(items[0].id));
              return next;
            },
            { replace: true },
          );
        }
      } catch {
        if (!cancelled) {
          setOrganizations([]);
        }
      }
    }

    loadOrganizations();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  const loadTrends = useCallback(async (organizationId, riskNumber, historyRange, hideTest, requestId) => {
    if (!organizationId) {
      setTrends({ wifi: new Map(), nbiot: new Map() });
      setHistoryPayloads({ wifi: null, nbiot: null });
      return;
    }

    async function loadConnection(connectionType) {
      try {
        const query = new URLSearchParams({
          organization_id: String(organizationId),
          connection_type: connectionType,
          range: historyRange || 'month',
          risk_number: String(riskNumber || '1'),
          hide_test: hideTest === false ? '0' : '1',
        });
        const response = await apiFetch(`/api/firmware/stats/history?${query}`);
        const payload = await readJsonResponse(response, 'Firmware history');
        if (!response.ok) {
          return { payload: null, trends: new Map() };
        }
        return { payload, trends: buildVersionTrendMap(payload) };
      } catch {
        return { payload: null, trends: new Map() };
      }
    }

    const [wifi, nbiot] = await Promise.all([loadConnection('wifi'), loadConnection('nbiot')]);
    if (requestId !== loadRequestIdRef.current) {
      return;
    }
    setTrends({ wifi: wifi.trends, nbiot: nbiot.trends });
    setHistoryPayloads({ wifi: wifi.payload, nbiot: nbiot.payload });
  }, []);

  const loadStats = useCallback(async (query) => {
    const requestId = ++loadRequestIdRef.current;
    setIsLoading(true);
    setError('');
    setData(null);
    setAllRisksReport(null);
    setTrends({ wifi: new Map(), nbiot: new Map() });
    setHistoryPayloads({ wifi: null, nbiot: null });

    try {
      if (isAllRisksView(query.riskNumber)) {
        const response = await apiFetch(`/api/firmware/versions-report?${buildVersionsReportQuery(query)}`);
        const payload = await readJsonResponse(response, 'Versions report');
        if (requestId !== loadRequestIdRef.current) {
          return;
        }
        if (!response.ok) {
          throw new Error(payload.message || `HTTP ${response.status}`);
        }

        const [wifiHistory, nbiotHistory] = await Promise.all([
          loadAllRisksHistoryPayload({
            organizationId: query.organizationId,
            connectionType: 'wifi',
            historyRange: query.historyRange,
            hideTest: query.hideTest,
          }),
          loadAllRisksHistoryPayload({
            organizationId: query.organizationId,
            connectionType: 'nbiot',
            historyRange: query.historyRange,
            hideTest: query.hideTest,
          }),
        ]);
        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        setHistoryPayloads({ wifi: wifiHistory, nbiot: nbiotHistory });
        setAllRisksReport(attachAllRisksSeriesToReport(payload, wifiHistory, nbiotHistory));
        return true;
      }

      const response = await apiFetch(`/api/firmware/stats?${buildFirmwareStatsQuery(query)}`);
      const payload = await readJsonResponse(response, 'Firmware stats');

      if (requestId !== loadRequestIdRef.current) {
        return;
      }

      if (!response.ok) {
        throw new Error(payload.message || `HTTP ${response.status}`);
      }

      setData(payload);
      await loadTrends(
        query.organizationId,
        query.riskNumber,
        query.historyRange,
        query.hideTest,
        requestId,
      );
      return true;
    } catch (loadError) {
      if (requestId !== loadRequestIdRef.current) {
        return false;
      }
      setError(loadError.message || 'Не удалось загрузить сводку по версиям');
      setData(null);
      setAllRisksReport(null);
      return false;
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [loadTrends]);

  const statsDebouncePrimedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'status') {
      statusVisibleRef.current = false;
      return undefined;
    }
    if (!filters.organizationId) {
      return undefined;
    }
    const justShown = !statusVisibleRef.current;
    statusVisibleRef.current = true;
    const statsKey = JSON.stringify(filters);
    if (justShown && loadedStatsKeyRef.current === statsKey) {
      return undefined;
    }
    const delayMs =
      typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test'
        ? 0
        : statsDebouncePrimedRef.current
          ? FILTER_FETCH_DEBOUNCE_MS
          : 0;
    statsDebouncePrimedRef.current = true;
    const timer = setTimeout(() => {
      loadStats(filters).then((ok) => {
        if (ok) {
          loadedStatsKeyRef.current = statsKey;
        }
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [activeTab, loadStats, filters]);

  return (
    <section className="space-y-3">
      <RisksTopBar
        meta={
          showAllRisks && allRisksReport ? (
            <span>
              {formatCount((allRisksReport.wifi?.total || 0) + (allRisksReport.nbiot?.total || 0))} устройств ·
              все риски
            </span>
          ) : data ? (
            <span>
              {formatCount(data.total)} устройств · в рисках {formatCount(data.at_risk)}
              {data.total ? (
                <>
                  {' '}
                  (<RiskPercent value={riskShare(data.at_risk, data.total)} className="text-xs" />)
                </>
              ) : null}
            </span>
          ) : null
        }
        actions={
          <div className="flex w-full min-w-0 flex-wrap items-end justify-end gap-2 lg:flex-nowrap">
            <label className="flex w-[11rem] shrink-0 flex-col gap-1">
              <span className="text-[11px] font-medium leading-none text-slate-500">Организация</span>
              <select
                aria-label="Управляющая компания"
                value={filters.organizationId}
                disabled={isLoading && organizations.length === 0}
                onChange={(event) => applyFilters({ organizationId: event.target.value })}
                className="h-9 w-full truncate rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:border-sky-500"
              >
                {organizations.length === 0 ? <option value="">Загрузка…</option> : null}
                {organizations.map((organization) => (
                  <option key={organization.id} value={String(organization.id)}>
                    {organization.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex w-[10rem] shrink-0 flex-col gap-1">
              <span className="text-[11px] font-medium leading-none text-slate-500">Риск</span>
              <select
                aria-label="Риск"
                value={filters.riskNumber}
                disabled={isLoading}
                onChange={(event) => applyFilters({ riskNumber: event.target.value })}
                className="h-9 w-full truncate rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                {FIRMWARE_RISK_NUMBER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex w-[6.5rem] shrink-0 flex-col gap-1">
              <span className="text-[11px] font-medium leading-none text-slate-500">Период</span>
              <select
                aria-label="Период графика"
                value={filters.historyRange}
                disabled={isLoading}
                onChange={(event) => applyFilters({ historyRange: event.target.value })}
                className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <option value="month">Месяц</option>
                <option value="year">Год</option>
                <option value="all">Весь период</option>
              </select>
            </label>
            <div className="flex shrink-0 items-end gap-2">
              <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-800 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={filters.hideTest !== false}
                  disabled={isLoading}
                  onChange={(event) => applyFilters({ hideTest: event.target.checked })}
                  className="h-3.5 w-3.5 rounded border-slate-300 bg-white text-emerald-500 focus:ring-emerald-400/40 dark:border-slate-600 dark:bg-slate-900"
                />
                Без теста
              </label>
              <button
                type="button"
                disabled={isLoading || !filters.organizationId}
                onClick={() => loadStats(filters)}
                className="inline-flex h-9 w-[6.75rem] shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Обновить
              </button>
            </div>
          </div>
        }
      />

      <div className="mx-auto max-w-6xl space-y-4">
        {error ? (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-200">
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <StatusVersionsSkeleton />
        ) : showAllRisks && allRisksReport ? (
          <div className="space-y-6">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)] lg:items-stretch">
              <VersionsAllRisksTable
                report={{ ...allRisksReport, nbiot: null, wifi: allRisksReport.wifi }}
                organizationId={filters.organizationId}
                VersionCell={AllRisksVersionCell}
              />
              <FirmwareRiskHistoryChart
                organizationId={filters.organizationId}
                connectionType="wifi"
                label="Wi-Fi"
                hideTest={filters.hideTest}
                riskNumber="all"
                historyRange={filters.historyRange}
                historyPayload={historyPayloads.wifi}
              />
            </div>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)] lg:items-stretch">
              <VersionsAllRisksTable
                report={{ ...allRisksReport, wifi: null, nbiot: allRisksReport.nbiot }}
                organizationId={filters.organizationId}
                VersionCell={AllRisksVersionCell}
              />
              <FirmwareRiskHistoryChart
                organizationId={filters.organizationId}
                connectionType="nbiot"
                label="NB-IoT"
                hideTest={filters.hideTest}
                riskNumber="all"
                historyRange={filters.historyRange}
                historyPayload={historyPayloads.nbiot}
              />
            </div>
          </div>
        ) : data ? (
          <div className="space-y-6">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)] lg:items-stretch">
              <VersionTable
                group={{ key: 'wifi', label: 'Wi-Fi', ...data.groups?.wifi }}
                organizationId={filters.organizationId}
                riskNumber={filters.riskNumber}
                onOpenVersion={openDashboardVersion}
                trendsByVersion={trends.wifi}
              />
              <FirmwareRiskHistoryChart
                organizationId={filters.organizationId}
                connectionType="wifi"
                label="Wi-Fi"
                hideTest={filters.hideTest}
                riskNumber={filters.riskNumber}
                historyRange={filters.historyRange}
                historyPayload={historyPayloads.wifi}
              />
            </div>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)] lg:items-stretch">
              <VersionTable
                group={{ key: 'nbiot', label: 'NB-IoT', ...data.groups?.nbiot }}
                organizationId={filters.organizationId}
                riskNumber={filters.riskNumber}
                onOpenVersion={openDashboardVersion}
                trendsByVersion={trends.nbiot}
              />
              <FirmwareRiskHistoryChart
                organizationId={filters.organizationId}
                connectionType="nbiot"
                label="NB-IoT"
                hideTest={filters.hideTest}
                riskNumber={filters.riskNumber}
                historyRange={filters.historyRange}
                historyPayload={historyPayloads.nbiot}
              />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
